import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { sendVerificationEmail } from '../../ducks/user.duck';
import {
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperAccountSettingsSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
  Modal,
} from '../../components';
import {
  AccountContactForm,
  AccountLinksForm,
  AccountPasswordForm,
  AccountVisibilityForm,
} from '../../forms';
import { TopbarContainer } from '../../containers';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';

import {
  saveContactDetails,
  saveContactDetailsClear,
  changePassword,
  changePasswordClear,
  deleteAccount,
  saveLinks,
  saveLinksClear,
  saveVisibility,
  saveVisibilityClear,
  loadData,
} from './AccountSettingsPage.duck';

import css from './AccountSettingsPage.css';

const AccountRoleSection = props => {
  const { currentUser } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!ensuredCurrentUser.id;

  const isCompanyAbroad = currentUserLoaded
    ? ensuredCurrentUser.attributes.profile?.privateData?.isCompanyAbroad
    : false;
  const baseAccountRole = currentUserLoaded
    ? ensuredCurrentUser.attributes.profile?.publicData?.account_role || 'freelancer'
    : 'freelancer';
  const accountRole = isCompanyAbroad ? 'company-abroad' : baseAccountRole;

  return (
    <div className={css.roleContent}>
      <p className={css.roleName}>
        <FormattedMessage id={`AccountRolePage.role.${accountRole}`} />
      </p>
      <p className={css.roleDescription}>
        <FormattedMessage id={`AccountRolePage.roleDescription.${accountRole}`} />
      </p>
      <p className={css.contactText}>
        <FormattedMessage id="AccountRolePage.contactPrompt" />{' '}
        <a href="mailto:hello@freedomly.io" className={css.contactLink}>
          hello@freedomly.io
        </a>
      </p>
    </div>
  );
};

const Section = ({ heading, description, children, className }) => (
  <section className={`${css.section} ${className || ''}`}>
    <h2 className={css.sectionHeading}>{heading}</h2>
    {description ? <p className={css.sectionDescription}>{description}</p> : null}
    <div className={css.sectionBody}>{children}</div>
  </section>
);

export const AccountSettingsPageComponent = props => {
  const {
    saveEmailError,
    savePhoneNumberError,
    saveContactDetailsInProgress,
    contactDetailsChanged,
    changePasswordError,
    changePasswordInProgress,
    passwordChanged,
    saveLinksInProgress,
    saveLinksError,
    linksSaved,
    saveVisibilityInProgress,
    saveVisibilityError,
    visibilitySaved,
    deleteAccountInProgress,
    deleteAccountError,
    currentUser,
    scrollingDisabled,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onSubmitContactDetails,
    onSubmitChangePassword,
    onSubmitLinks,
    onSubmitVisibility,
    onDeleteAccount,
    onManageDisableScrolling,
    onChangeContact,
    onChangePassword,
    onChangeLinks,
    onChangeVisibility,
    intl,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const userLoaded = !!user.id;

  const currentEmail = user.attributes.email || '';
  const protectedData = user.attributes.profile?.protectedData || {};
  const publicData = user.attributes.profile?.publicData || {};
  const metadata = user.attributes.profile?.metadata || {};

  const currentPhoneNumber = protectedData.phoneNumber || '';
  const currentTextMessageNotifications = protectedData.textMessageNotifications;
  const currentWebsite = protectedData.website || '';
  const currentLinkedIn = protectedData.linkedIn || '';
  const currentNoIndexing = !!publicData.noIndexing;
  const currentShowAtMarketPlace = metadata.showAtMarketPlace !== false;
  const currentHideProfile = currentNoIndexing || !currentShowAtMarketPlace;

  const accountRole = publicData.account_role;
  const isSeller = accountRole === 'freelancer' || accountRole === 'light-entrepreneur';

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  const deleteAccountErrorMessage = deleteAccountError
    ? deleteAccountError.status === 403
      ? intl.formatMessage({ id: 'ContactDetailsForm.deleteAccountWrongPassword' })
      : intl.formatMessage({ id: 'ContactDetailsForm.deleteAccountError' })
    : null;

  const title = intl.formatMessage({ id: 'AccountSettingsPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="AccountSettingsPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperAccountSettingsSideNav
          currentTab="AccountSettingsPage"
          currentUser={currentUser}
        />
        <LayoutWrapperMain>
          <div className={css.content}>
            <h1 className={css.title}>
              <FormattedMessage id="AccountSettingsPage.heading" />
            </h1>

            {isSeller ? (
              <Section heading={<FormattedMessage id="AccountSettingsPage.accountRoleHeading" />}>
                <AccountRoleSection currentUser={currentUser} />
              </Section>
            ) : null}

            {userLoaded ? (
              <Section
                heading={<FormattedMessage id="AccountSettingsPage.contactHeading" />}
                description={<FormattedMessage id="AccountSettingsPage.contactDescription" />}
              >
                <AccountContactForm
                  initialValues={{
                    email: currentEmail,
                    phoneNumber: currentPhoneNumber,
                    textMessageNotifications: currentTextMessageNotifications,
                  }}
                  saveEmailError={saveEmailError}
                  savePhoneNumberError={savePhoneNumberError}
                  currentUser={currentUser}
                  onResendVerificationEmail={onResendVerificationEmail}
                  onSubmit={values =>
                    onSubmitContactDetails({
                      ...values,
                      currentEmail,
                      currentPhoneNumber,
                      currentTextMessageNotifications,
                    })
                  }
                  onChange={onChangeContact}
                  inProgress={saveContactDetailsInProgress}
                  ready={contactDetailsChanged}
                  sendVerificationEmailInProgress={sendVerificationEmailInProgress}
                  sendVerificationEmailError={sendVerificationEmailError}
                />
              </Section>
            ) : null}

            {userLoaded && isSeller ? (
              <Section
                heading={<FormattedMessage id="AccountSettingsPage.linksHeading" />}
                description={<FormattedMessage id="AccountSettingsPage.linksDescription" />}
              >
                <AccountLinksForm
                  initialValues={{ website: currentWebsite, linkedIn: currentLinkedIn }}
                  onSubmit={values => onSubmitLinks(values)}
                  onChange={onChangeLinks}
                  inProgress={saveLinksInProgress}
                  ready={linksSaved}
                />
                {saveLinksError ? (
                  <p className={css.formError}>
                    <FormattedMessage id="AccountSettingsPage.linksError" />
                  </p>
                ) : null}
              </Section>
            ) : null}

            {userLoaded ? (
              <Section
                heading={<FormattedMessage id="AccountSettingsPage.passwordHeading" />}
                description={<FormattedMessage id="AccountSettingsPage.passwordDescription" />}
              >
                <AccountPasswordForm
                  changePasswordError={changePasswordError}
                  currentUser={currentUser}
                  onSubmit={onSubmitChangePassword}
                  onChange={onChangePassword}
                  inProgress={changePasswordInProgress}
                  ready={passwordChanged}
                />
              </Section>
            ) : null}

            {userLoaded && isSeller ? (
              <Section heading={<FormattedMessage id="AccountSettingsPage.visibilityHeading" />}>
                <AccountVisibilityForm
                  initialValues={{ hideProfile: currentHideProfile }}
                  onSubmit={values =>
                    onSubmitVisibility({
                      userId: user.id.uuid,
                      noIndexing: !!values.hideProfile,
                      showAtMarketPlace: !values.hideProfile,
                    })
                  }
                  onChange={onChangeVisibility}
                  inProgress={saveVisibilityInProgress}
                  ready={visibilitySaved}
                  saveError={saveVisibilityError}
                />
              </Section>
            ) : null}

            {userLoaded ? (
              <Section
                heading={<FormattedMessage id="AccountSettingsPage.deleteAccountHeading" />}
                description={<FormattedMessage id="AccountSettingsPage.deleteAccountDescription" />}
              >
                <div className={css.deleteAccountRow}>
                  <button
                    type="button"
                    className={css.deleteAccountButton}
                    onClick={() => {
                      setIsDeleteModalOpen(true);
                      setDeletePassword('');
                    }}
                  >
                    <FormattedMessage id="ContactDetailsForm.deleteAccountButton" />
                  </button>
                </div>
              </Section>
            ) : null}

            {userLoaded ? (
              <Modal
                id="AccountSettingsDeleteAccountModal"
                isOpen={isDeleteModalOpen}
                onClose={() => {
                  setIsDeleteModalOpen(false);
                  setDeletePassword('');
                }}
                onManageDisableScrolling={onManageDisableScrolling}
                usePortal
              >
                <div className={css.deleteAccountModal}>
                  <h2 className={css.deleteAccountModalTitle}>
                    <FormattedMessage id="ContactDetailsForm.deleteAccountModalTitle" />
                  </h2>
                  <ul className={css.deleteAccountInfo}>
                    <li>
                      <FormattedMessage
                        id={
                          isSeller
                            ? 'ContactDetailsForm.deleteAccountInfoGeneral1'
                            : 'ContactDetailsForm.deleteAccountInfoCustomer1'
                        }
                      />
                    </li>
                    <li>
                      <FormattedMessage id="ContactDetailsForm.deleteAccountInfoGeneral2" />
                    </li>
                    <li>
                      <FormattedMessage id="ContactDetailsForm.deleteAccountInfoGeneral3" />
                    </li>
                  </ul>
                  {isSeller ? (
                    <ul className={css.deleteAccountInfo}>
                      <li>
                        <FormattedMessage id="ContactDetailsForm.deleteAccountInfoSeller1" />
                      </li>
                      <li>
                        <FormattedMessage id="ContactDetailsForm.deleteAccountInfoSeller2" />
                      </li>
                      <li>
                        <FormattedMessage id="ContactDetailsForm.deleteAccountInfoSeller3" />
                      </li>
                      <li>
                        <FormattedMessage id="ContactDetailsForm.deleteAccountInfoSeller4" />
                      </li>
                    </ul>
                  ) : null}
                  <p className={css.deleteAccountSupport}>
                    <FormattedMessage id="ContactDetailsForm.deleteAccountSupport" />
                  </p>
                  <label className={css.deleteAccountPasswordLabel}>
                    <FormattedMessage id="ContactDetailsForm.deleteAccountPasswordLabel" />
                  </label>
                  <input
                    type="password"
                    className={css.deleteAccountPasswordInput}
                    placeholder={intl.formatMessage({
                      id: 'ContactDetailsForm.deleteAccountPasswordPlaceholder',
                    })}
                    value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                  />
                  {deleteAccountErrorMessage ? (
                    <p className={css.deleteAccountError}>{deleteAccountErrorMessage}</p>
                  ) : null}
                  <div className={css.deleteAccountActions}>
                    <button
                      type="button"
                      className={css.deleteAccountConfirmButton}
                      disabled={deleteAccountInProgress || !deletePassword}
                      onClick={() => onDeleteAccount(deletePassword)}
                    >
                      {deleteAccountInProgress
                        ? '...'
                        : intl.formatMessage({ id: 'ContactDetailsForm.deleteAccountConfirm' })}
                    </button>
                    <button
                      type="button"
                      className={css.deleteAccountCancelButton}
                      onClick={() => {
                        setIsDeleteModalOpen(false);
                        setDeletePassword('');
                      }}
                    >
                      <FormattedMessage id="ContactDetailsForm.deleteAccountCancel" />
                    </button>
                  </div>
                </div>
              </Modal>
            ) : null}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

AccountSettingsPageComponent.defaultProps = {
  saveEmailError: null,
  savePhoneNumberError: null,
  changePasswordError: null,
  saveLinksError: null,
  saveVisibilityError: null,
  currentUser: null,
  sendVerificationEmailError: null,
  deleteAccountInProgress: false,
  deleteAccountError: null,
};

const { bool, func } = PropTypes;

AccountSettingsPageComponent.propTypes = {
  saveEmailError: propTypes.error,
  savePhoneNumberError: propTypes.error,
  saveContactDetailsInProgress: bool.isRequired,
  contactDetailsChanged: bool.isRequired,
  changePasswordError: propTypes.error,
  changePasswordInProgress: bool.isRequired,
  passwordChanged: bool.isRequired,
  saveLinksInProgress: bool.isRequired,
  saveLinksError: propTypes.error,
  linksSaved: bool.isRequired,
  saveVisibilityInProgress: bool.isRequired,
  saveVisibilityError: propTypes.error,
  visibilitySaved: bool.isRequired,
  currentUser: propTypes.currentUser,
  onResendVerificationEmail: func.isRequired,
  onSubmitContactDetails: func.isRequired,
  onSubmitChangePassword: func.isRequired,
  onSubmitLinks: func.isRequired,
  onSubmitVisibility: func.isRequired,
  onDeleteAccount: func.isRequired,
  onChangeContact: func.isRequired,
  onChangePassword: func.isRequired,
  onChangeLinks: func.isRequired,
  onChangeVisibility: func.isRequired,
  scrollingDisabled: bool.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,

  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const {
    saveEmailError,
    savePhoneNumberError,
    saveContactDetailsInProgress,
    contactDetailsChanged,
    changePasswordError,
    changePasswordInProgress,
    passwordChanged,
    deleteAccountInProgress,
    deleteAccountError,
    saveLinksInProgress,
    saveLinksError,
    linksSaved,
    saveVisibilityInProgress,
    saveVisibilityError,
    visibilitySaved,
  } = state.AccountSettingsPage;

  return {
    saveEmailError,
    savePhoneNumberError,
    saveContactDetailsInProgress,
    contactDetailsChanged,
    changePasswordError,
    changePasswordInProgress,
    passwordChanged,
    deleteAccountInProgress,
    deleteAccountError,
    saveLinksInProgress,
    saveLinksError,
    linksSaved,
    saveVisibilityInProgress,
    saveVisibilityError,
    visibilitySaved,
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  };
};

const mapDispatchToProps = dispatch => ({
  onChangeContact: () => dispatch(saveContactDetailsClear()),
  onChangePassword: () => dispatch(changePasswordClear()),
  onChangeLinks: () => dispatch(saveLinksClear()),
  onChangeVisibility: () => dispatch(saveVisibilityClear()),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onSubmitContactDetails: values => dispatch(saveContactDetails(values)),
  onSubmitChangePassword: values => dispatch(changePassword(values)),
  onSubmitLinks: values => dispatch(saveLinks(values)),
  onSubmitVisibility: values => dispatch(saveVisibility(values)),
  onDeleteAccount: currentPassword => dispatch(deleteAccount(currentPassword)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const AccountSettingsPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(AccountSettingsPageComponent);

AccountSettingsPage.loadData = loadData;

export default AccountSettingsPage;
